/**
 * Copy de la página «Sobre nosotros» (`/sobre-nosotros` y sus cuatro
 * traducciones).
 *
 * Es la página institucional: la que lee quien ya conoce el producto y quiere
 * saber QUIÉN hay detrás antes de confiarle su ficha de Google. Por eso habla
 * del equipo, de Valencia y de los dispositivos, y no repite el argumentario de
 * la home.
 *
 * ⚠️ Lo que NO dice, a propósito:
 *  - **Nombres de fundadores.** Decisión de producto: se habla del equipo.
 *  - **Cifras nuevas.** Las únicas que salen son las de la home (`home.cifras`),
 *    que siguen marcadas como sin confirmar; aquí no se inventa ninguna más.
 *  - **Promesas de infraestructura** («servidores en la UE», «soporte 24/7»…)
 *    que nadie ha contrastado. Lo del RGPD sí: el DPA está publicado en `/dpa`.
 */

/**
 * El vídeo de presentación. NO es copy: son rutas y metadatos, iguales en los
 * cinco idiomas, y por eso viven fuera del árbol de traducciones.
 *
 * Mientras `src` sea `null` la página pinta el hueco del vídeo con un «muy
 * pronto» diseñado, y NO se emite el `VideoObject` de schema.org (Google lo
 * rechaza sin `contentUrl`, `thumbnailUrl` y `uploadDate`, y anunciar un vídeo
 * que no existe es peor que no anunciar nada).
 *
 * Para publicarlo, antes de desplegar:
 *  1. Deja el mp4 (H.264, idealmente ≤ 15 MB) y un póster JPG/WebP de 1280×720
 *     en `public/landing-images/v3/sobre-nosotros/`.
 *  2. Rellena los cinco campos de abajo. `duracion` va en ISO 8601 (`PT1M45S`)
 *     y `subido` es la fecha de publicación (`2026-10-20`).
 *  3. `subtitulos` es opcional: un `.vtt` en español mejora la accesibilidad y
 *     le da a Google el texto del vídeo.
 */
export const VIDEO_SOBRE_NOSOTROS = {
  src: null, // p. ej. '/landing-images/v3/sobre-nosotros/presentacion.mp4'
  poster: null, // p. ej. '/landing-images/v3/sobre-nosotros/presentacion-poster.jpg'
  subtitulos: null, // p. ej. '/landing-images/v3/sobre-nosotros/presentacion.es.vtt'
  duracion: null, // p. ej. 'PT1M45S'
  subido: null, // p. ej. '2026-10-20'
};

/**
 * La foto del equipo. Igual que el vídeo: mientras sea `null`, el bloque de
 * Valencia pinta el plano con la chincheta en vez de la foto.
 */
export const FOTO_EQUIPO = null; // p. ej. '/landing-images/v3/sobre-nosotros/equipo.webp'

const es = {
  hero: {
    kicker: 'Sobre nosotros',
    tituloA: 'Ayudamos a los negocios locales a que ',
    acento: 'Google hable bien de ellos',
    tituloB: '.',
    subtitulo:
      'Somos MyTapstar, un equipo de Valencia que diseña dispositivos y software para que cada cliente satisfecho se convierta en una reseña, y cada reseña, en más visitas a tu negocio.',
    ctaPrimario: 'Probar 7 días gratis',
    ctaSecundario: 'Ver el vídeo',
  },

  video: {
    titulo: 'Conócenos en vídeo',
    desc: 'Quiénes somos, qué hacemos y cómo trabajamos con los negocios que confían en nosotros.',
    proximamente: 'Muy pronto, nuestro vídeo aquí',
    reproducir: 'Reproducir el vídeo',
  },

  historia: {
    kicker: 'De dónde venimos',
    titulo: 'Empezamos en un mostrador',
    parrafos: [
      'MyTapstar nació en Valencia con una idea sencilla: a un cliente contento solo le falta un empujón para dejar su reseña. Si se lo pides en el momento justo y se lo pones fácil, lo hace.',
      'Por eso empezamos por los dispositivos Tapstar. Un toque con el móvil o un escaneo del código QR y el cliente aterriza directamente en la ficha de Google del negocio, listo para valorar. Sin buscar el nombre y sin descargar nada.',
      'Con los primeros negocios llegaron las preguntas: ¿cuántas reseñas trae cada dispositivo?, ¿quién del equipo consigue más?, ¿cómo respondo a todas?, ¿por qué mi competencia sale antes en el mapa? De esas preguntas nació la plataforma.',
    ],
    destacado:
      'Hoy unimos hardware y software en un mismo sitio: del primer toque en el mostrador a la posición que ocupas en Google Maps.',
  },

  hacemos: {
    kicker: 'Lo que hacemos',
    titulo: 'Tres piezas que trabajan juntas',
    items: [
      {
        clave: 'dispositivos',
        titulo: 'Dispositivos Tapstar',
        desc: 'Con NFC y código QR, para el mostrador, las mesas o cada persona de tu equipo. Tus clientes dejan su reseña en segundos.',
      },
      {
        clave: 'plataforma',
        titulo: 'La plataforma',
        desc: 'Todas tus reseñas en una bandeja, respuestas con IA, el Mapa SEO, publicaciones en tu ficha y las métricas de cada local.',
      },
      {
        clave: 'equipo',
        titulo: 'Un equipo cerca',
        desc: 'Detrás hay personas. Te ayudamos a ponerlo en marcha y a sacarle partido, desde Valencia.',
      },
    ],
  },

  principios: {
    kicker: 'Cómo trabajamos',
    titulo: 'Lo que no negociamos',
    items: [
      {
        clave: 'reales',
        titulo: 'Solo reseñas reales',
        desc: 'No compramos ni inventamos opiniones. Te ayudamos a pedírselas a tus clientes de verdad, que son las que cuentan para Google y para quien te lee.',
      },
      {
        clave: 'medible',
        titulo: 'Todo se mide',
        desc: 'Escaneos, reseñas, respuestas y posición en el mapa: lo que hacemos tiene que verse en tus números, no en una promesa.',
      },
      {
        clave: 'libertad',
        titulo: 'Sin permanencia',
        desc: 'Te quedas porque te funciona. Puedes cambiar de plan o cancelar cuando quieras.',
      },
      {
        clave: 'datos',
        titulo: 'Tus datos son tuyos',
        desc: 'Cumplimos el RGPD y firmamos contigo el contrato de encargo del tratamiento. Lo que pasa en tu ficha es tuyo.',
      },
    ],
  },

  valencia: {
    kicker: 'Hecho en Valencia',
    titulo: 'Desde Valencia, para negocios de toda España y Portugal',
    desc: 'Diseñamos, desarrollamos y damos soporte desde Valencia. Formamos parte de Lanzadera, la aceleradora de Marina de Empresas, donde tenemos nuestra sede.',
    oficina: 'Oficina',
    sede: 'Sede',
    respaldo: 'Respaldado por',
    altLanzadera: 'Lanzadera',
    contacto: '¿Quieres hablar con nosotros?',
    contactoCta: 'Escríbenos',
    fotoAlt: 'El equipo de MyTapstar en Valencia',
  },

  cierre: {
    tituloA: 'Ya nos conoces. ',
    acento: 'Ahora, pruébalo.',
  },
};

const en = {
  hero: {
    kicker: 'About us',
    tituloA: 'We help local businesses get ',
    acento: 'Google talking about them',
    tituloB: '.',
    subtitulo:
      'We are MyTapstar, a team in Valencia designing devices and software so that every happy customer becomes a review, and every review becomes more visits to your business.',
    ctaPrimario: 'Try 7 days free',
    ctaSecundario: 'Watch the video',
  },

  video: {
    titulo: 'Meet us on video',
    desc: 'Who we are, what we do and how we work with the businesses that trust us.',
    proximamente: 'Our video is coming soon',
    reproducir: 'Play the video',
  },

  historia: {
    kicker: 'Where we come from',
    titulo: 'It all started at a counter',
    parrafos: [
      'MyTapstar was born in Valencia from a simple idea: a happy customer only needs a nudge to leave a review. Ask at the right moment, make it easy, and they will.',
      'That is why we started with Tapstar devices. One tap with a phone or a scan of the QR code and the customer lands straight on the business’s Google profile, ready to rate it. No searching for the name, nothing to download.',
      'With our first businesses came the questions: how many reviews does each device bring in? Who on the team gets the most? How do I reply to all of them? Why does my competitor show up first on the map? Those questions gave birth to the platform.',
    ],
    destacado:
      'Today we bring hardware and software together in one place: from the first tap at the counter to where you rank on Google Maps.',
  },

  hacemos: {
    kicker: 'What we do',
    titulo: 'Three pieces that work together',
    items: [
      {
        clave: 'dispositivos',
        titulo: 'Tapstar devices',
        desc: 'With NFC and a QR code, for the counter, the tables or each member of your team. Your customers leave their review in seconds.',
      },
      {
        clave: 'plataforma',
        titulo: 'The platform',
        desc: 'All your reviews in one inbox, AI replies, the SEO Map, posts on your profile and metrics for every location.',
      },
      {
        clave: 'equipo',
        titulo: 'A team close by',
        desc: 'There are people behind it. We help you get started and make the most of it, from Valencia.',
      },
    ],
  },

  principios: {
    kicker: 'How we work',
    titulo: 'What we won’t compromise on',
    items: [
      {
        clave: 'reales',
        titulo: 'Only real reviews',
        desc: 'We never buy or make up reviews. We help you ask your real customers, because theirs are the ones that count for Google and for your readers.',
      },
      {
        clave: 'medible',
        titulo: 'Everything is measured',
        desc: 'Scans, reviews, replies and map position: what we do has to show up in your numbers, not in a promise.',
      },
      {
        clave: 'libertad',
        titulo: 'No minimum term',
        desc: 'You stay because it works for you. Change plans or cancel whenever you like.',
      },
      {
        clave: 'datos',
        titulo: 'Your data is yours',
        desc: 'We comply with the GDPR and sign a data processing agreement with you. What happens on your profile belongs to you.',
      },
    ],
  },

  valencia: {
    kicker: 'Made in Valencia',
    titulo: 'From Valencia, for businesses across Spain and Portugal',
    desc: 'We design, build and support everything from Valencia. We are part of Lanzadera, the Marina de Empresas accelerator, where our headquarters are.',
    oficina: 'Office',
    sede: 'Headquarters',
    respaldo: 'Backed by',
    altLanzadera: 'Lanzadera',
    contacto: 'Want to talk to us?',
    contactoCta: 'Write to us',
    fotoAlt: 'The MyTapstar team in Valencia',
  },

  cierre: {
    tituloA: 'Now you know us. ',
    acento: 'Time to try it.',
  },
};

const pt = {
  hero: {
    kicker: 'Sobre nós',
    tituloA: 'Ajudamos os negócios locais a que ',
    acento: 'o Google fale bem deles',
    tituloB: '.',
    subtitulo:
      'Somos a MyTapstar, uma equipa de Valência que desenha dispositivos e software para que cada cliente satisfeito se transforme numa avaliação e cada avaliação em mais visitas ao seu negócio.',
    ctaPrimario: 'Experimentar 7 dias grátis',
    ctaSecundario: 'Ver o vídeo',
  },

  video: {
    titulo: 'Conheça-nos em vídeo',
    desc: 'Quem somos, o que fazemos e como trabalhamos com os negócios que confiam em nós.',
    proximamente: 'Muito em breve, o nosso vídeo aqui',
    reproducir: 'Reproduzir o vídeo',
  },

  historia: {
    kicker: 'De onde vimos',
    titulo: 'Começámos num balcão',
    parrafos: [
      'A MyTapstar nasceu em Valência com uma ideia simples: a um cliente satisfeito só falta um empurrão para deixar a sua avaliação. Se lhe pedir no momento certo e lhe facilitar a vida, ele deixa.',
      'Por isso começámos pelos dispositivos Tapstar. Um toque com o telemóvel ou a leitura do código QR e o cliente chega diretamente à ficha do Google do negócio, pronto para avaliar. Sem procurar o nome e sem descarregar nada.',
      'Com os primeiros negócios chegaram as perguntas: quantas avaliações traz cada dispositivo? Quem da equipa consegue mais? Como respondo a todas? Porque é que a concorrência aparece primeiro no mapa? Dessas perguntas nasceu a plataforma.',
    ],
    destacado:
      'Hoje juntamos hardware e software no mesmo sítio: do primeiro toque no balcão à posição que ocupa no Google Maps.',
  },

  hacemos: {
    kicker: 'O que fazemos',
    titulo: 'Três peças que trabalham juntas',
    items: [
      {
        clave: 'dispositivos',
        titulo: 'Dispositivos Tapstar',
        desc: 'Com NFC e código QR, para o balcão, as mesas ou cada pessoa da sua equipa. Os seus clientes deixam a avaliação em segundos.',
      },
      {
        clave: 'plataforma',
        titulo: 'A plataforma',
        desc: 'Todas as suas avaliações numa caixa, respostas com IA, o Mapa SEO, publicações na sua ficha e as métricas de cada estabelecimento.',
      },
      {
        clave: 'equipo',
        titulo: 'Uma equipa próxima',
        desc: 'Por trás há pessoas. Ajudamo-lo a pôr tudo a funcionar e a tirar o máximo partido, a partir de Valência.',
      },
    ],
  },

  principios: {
    kicker: 'Como trabalhamos',
    titulo: 'O que não negociamos',
    items: [
      {
        clave: 'reales',
        titulo: 'Só avaliações reais',
        desc: 'Não compramos nem inventamos opiniões. Ajudamo-lo a pedi-las aos seus clientes de verdade, que são as que contam para o Google e para quem o lê.',
      },
      {
        clave: 'medible',
        titulo: 'Tudo se mede',
        desc: 'Leituras, avaliações, respostas e posição no mapa: o que fazemos tem de se ver nos seus números, não numa promessa.',
      },
      {
        clave: 'libertad',
        titulo: 'Sem fidelização',
        desc: 'Fica porque funciona. Pode mudar de plano ou cancelar quando quiser.',
      },
      {
        clave: 'datos',
        titulo: 'Os seus dados são seus',
        desc: 'Cumprimos o RGPD e assinamos consigo o contrato de subcontratação do tratamento. O que acontece na sua ficha é seu.',
      },
    ],
  },

  valencia: {
    kicker: 'Feito em Valência',
    titulo: 'De Valência, para negócios de toda a Espanha e Portugal',
    desc: 'Desenhamos, desenvolvemos e damos suporte a partir de Valência. Fazemos parte da Lanzadera, a aceleradora da Marina de Empresas, onde temos a nossa sede.',
    oficina: 'Escritório',
    sede: 'Sede',
    respaldo: 'Com o apoio de',
    altLanzadera: 'Lanzadera',
    contacto: 'Quer falar connosco?',
    contactoCta: 'Escreva-nos',
    fotoAlt: 'A equipa da MyTapstar em Valência',
  },

  cierre: {
    tituloA: 'Já nos conhece. ',
    acento: 'Agora, experimente.',
  },
};

const de = {
  hero: {
    kicker: 'Über uns',
    tituloA: 'Wir sorgen dafür, dass ',
    acento: 'Google gut über lokale Unternehmen spricht',
    tituloB: '.',
    subtitulo:
      'Wir sind MyTapstar, ein Team aus Valencia, das Geräte und Software entwickelt, damit aus jedem zufriedenen Kunden eine Bewertung wird – und aus jeder Bewertung mehr Besuche in deinem Geschäft.',
    ctaPrimario: '7 Tage kostenlos testen',
    ctaSecundario: 'Video ansehen',
  },

  video: {
    titulo: 'Lern uns im Video kennen',
    desc: 'Wer wir sind, was wir machen und wie wir mit den Unternehmen arbeiten, die uns vertrauen.',
    proximamente: 'Unser Video kommt in Kürze',
    reproducir: 'Video abspielen',
  },

  historia: {
    kicker: 'Woher wir kommen',
    titulo: 'Angefangen hat alles an einer Theke',
    parrafos: [
      'MyTapstar ist in Valencia aus einer einfachen Idee entstanden: Einem zufriedenen Kunden fehlt nur ein kleiner Anstoß, um eine Bewertung zu hinterlassen. Fragst du im richtigen Moment und machst es ihm leicht, tut er es.',
      'Deshalb haben wir mit den Tapstar-Geräten angefangen. Ein Tippen mit dem Handy oder ein Scan des QR-Codes, und der Kunde landet direkt im Google-Profil des Unternehmens, bereit zu bewerten. Ohne nach dem Namen zu suchen und ohne etwas herunterzuladen.',
      'Mit den ersten Unternehmen kamen die Fragen: Wie viele Bewertungen bringt jedes Gerät? Wer im Team holt die meisten? Wie antworte ich auf alle? Warum steht die Konkurrenz auf der Karte vor mir? Aus diesen Fragen ist die Plattform entstanden.',
    ],
    destacado:
      'Heute bringen wir Hardware und Software an einem Ort zusammen: vom ersten Tippen an der Theke bis zu deiner Position in Google Maps.',
  },

  hacemos: {
    kicker: 'Was wir machen',
    titulo: 'Drei Bausteine, die zusammenarbeiten',
    items: [
      {
        clave: 'dispositivos',
        titulo: 'Tapstar-Geräte',
        desc: 'Mit NFC und QR-Code, für die Theke, die Tische oder jede Person in deinem Team. Deine Kunden bewerten in Sekunden.',
      },
      {
        clave: 'plataforma',
        titulo: 'Die Plattform',
        desc: 'Alle Bewertungen in einem Posteingang, Antworten mit KI, die SEO-Karte, Beiträge in deinem Profil und Kennzahlen für jeden Standort.',
      },
      {
        clave: 'equipo',
        titulo: 'Ein Team in deiner Nähe',
        desc: 'Dahinter stehen Menschen. Wir helfen dir beim Start und dabei, das Beste herauszuholen – aus Valencia.',
      },
    ],
  },

  principios: {
    kicker: 'Wie wir arbeiten',
    titulo: 'Worüber wir nicht verhandeln',
    items: [
      {
        clave: 'reales',
        titulo: 'Nur echte Bewertungen',
        desc: 'Wir kaufen keine Bewertungen und erfinden keine. Wir helfen dir, deine echten Kunden zu fragen – denn genau die zählen für Google und für alle, die sie lesen.',
      },
      {
        clave: 'medible',
        titulo: 'Alles wird gemessen',
        desc: 'Scans, Bewertungen, Antworten und Position auf der Karte: Was wir tun, muss sich in deinen Zahlen zeigen, nicht in einem Versprechen.',
      },
      {
        clave: 'libertad',
        titulo: 'Ohne Mindestlaufzeit',
        desc: 'Du bleibst, weil es funktioniert. Du kannst den Plan jederzeit wechseln oder kündigen.',
      },
      {
        clave: 'datos',
        titulo: 'Deine Daten gehören dir',
        desc: 'Wir halten die DSGVO ein und schließen mit dir einen Auftragsverarbeitungsvertrag. Was in deinem Profil passiert, gehört dir.',
      },
    ],
  },

  valencia: {
    kicker: 'Made in Valencia',
    titulo: 'Aus Valencia, für Unternehmen in ganz Spanien und Portugal',
    desc: 'Wir entwerfen, entwickeln und betreuen alles von Valencia aus. Wir sind Teil von Lanzadera, dem Accelerator der Marina de Empresas, wo unser Hauptsitz ist.',
    oficina: 'Büro',
    sede: 'Hauptsitz',
    respaldo: 'Unterstützt von',
    altLanzadera: 'Lanzadera',
    contacto: 'Möchtest du mit uns sprechen?',
    contactoCta: 'Schreib uns',
    fotoAlt: 'Das MyTapstar-Team in Valencia',
  },

  cierre: {
    tituloA: 'Jetzt kennst du uns. ',
    acento: 'Probier es aus.',
  },
};

const fr = {
  hero: {
    kicker: 'À propos',
    tituloA: 'Nous aidons les commerces locaux à ce que ',
    acento: 'Google parle bien d’eux',
    tituloB: '.',
    subtitulo:
      'Nous sommes MyTapstar, une équipe basée à Valence qui conçoit des appareils et un logiciel pour que chaque client satisfait devienne un avis, et chaque avis, plus de visites dans votre établissement.',
    ctaPrimario: 'Essayer 7 jours gratuits',
    ctaSecundario: 'Voir la vidéo',
  },

  video: {
    titulo: 'Découvrez-nous en vidéo',
    desc: 'Qui nous sommes, ce que nous faisons et comment nous travaillons avec les commerces qui nous font confiance.',
    proximamente: 'Notre vidéo arrive très bientôt',
    reproducir: 'Lire la vidéo',
  },

  historia: {
    kicker: 'D’où nous venons',
    titulo: 'Tout a commencé sur un comptoir',
    parrafos: [
      'MyTapstar est né à Valence d’une idée simple : un client satisfait n’a besoin que d’un petit coup de pouce pour laisser son avis. Si vous le lui demandez au bon moment et que vous lui facilitez la tâche, il le fait.',
      'C’est pourquoi nous avons commencé par les appareils Tapstar. Un geste avec le téléphone ou un scan du QR code, et le client arrive directement sur la fiche Google de l’établissement, prêt à donner sa note. Sans chercher le nom, sans rien télécharger.',
      'Avec les premiers commerces sont venues les questions : combien d’avis rapporte chaque appareil ? Qui, dans l’équipe, en obtient le plus ? Comment répondre à tous ? Pourquoi mon concurrent apparaît-il avant moi sur la carte ? C’est de ces questions qu’est née la plateforme.',
    ],
    destacado:
      'Aujourd’hui, nous réunissons matériel et logiciel au même endroit : du premier geste au comptoir jusqu’à votre position sur Google Maps.',
  },

  hacemos: {
    kicker: 'Ce que nous faisons',
    titulo: 'Trois éléments qui travaillent ensemble',
    items: [
      {
        clave: 'dispositivos',
        titulo: 'Appareils Tapstar',
        desc: 'Avec NFC et QR code, pour le comptoir, les tables ou chaque membre de votre équipe. Vos clients laissent leur avis en quelques secondes.',
      },
      {
        clave: 'plataforma',
        titulo: 'La plateforme',
        desc: 'Tous vos avis dans une seule boîte, des réponses par IA, la Carte SEO, des publications sur votre fiche et les indicateurs de chaque établissement.',
      },
      {
        clave: 'equipo',
        titulo: 'Une équipe proche',
        desc: 'Derrière, il y a des personnes. Nous vous aidons à démarrer et à en tirer le meilleur, depuis Valence.',
      },
    ],
  },

  principios: {
    kicker: 'Notre façon de travailler',
    titulo: 'Ce sur quoi nous ne transigeons pas',
    items: [
      {
        clave: 'reales',
        titulo: 'Uniquement des avis réels',
        desc: 'Nous n’achetons ni n’inventons aucun avis. Nous vous aidons à les demander à vos vrais clients, car ce sont eux qui comptent pour Google et pour vos lecteurs.',
      },
      {
        clave: 'medible',
        titulo: 'Tout se mesure',
        desc: 'Scans, avis, réponses et position sur la carte : ce que nous faisons doit se voir dans vos chiffres, pas dans une promesse.',
      },
      {
        clave: 'libertad',
        titulo: 'Sans engagement',
        desc: 'Vous restez parce que ça marche. Vous pouvez changer de plan ou résilier quand vous voulez.',
      },
      {
        clave: 'datos',
        titulo: 'Vos données vous appartiennent',
        desc: 'Nous respectons le RGPD et signons avec vous un accord de sous-traitance des données. Ce qui se passe sur votre fiche vous appartient.',
      },
    ],
  },

  valencia: {
    kicker: 'Fabriqué à Valence',
    titulo: 'Depuis Valence, pour les commerces de toute l’Espagne et du Portugal',
    desc: 'Nous concevons, développons et assurons le support depuis Valence. Nous faisons partie de Lanzadera, l’accélérateur de Marina de Empresas, où se trouve notre siège.',
    oficina: 'Bureau',
    sede: 'Siège',
    respaldo: 'Soutenu par',
    altLanzadera: 'Lanzadera',
    contacto: 'Envie de nous parler ?',
    contactoCta: 'Écrivez-nous',
    fotoAlt: 'L’équipe MyTapstar à Valence',
  },

  cierre: {
    tituloA: 'Vous nous connaissez maintenant. ',
    acento: 'À vous d’essayer.',
  },
};

export const sobreNosotrosV3 = { es, en, pt, de, fr };
